import React from 'react';
import { Target, Users, Heart, Eye, Leaf, Handshake, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { percheSocietaBenefit, benefitImages } from '../mockData';

const iconMap = {
  Target,
  Users,
  Heart,
  Eye,
  Leaf,
  Handshake
};

const SocietaBenefitSection = () => {
  return (
    <section id="societa-benefit" className="py-20 bg-gradient-to-b from-[#5FB76E]/5 to-white relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img
          src={benefitImages[3]}
          alt="Community"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-[#5FB76E]/10 text-[#5FB76E] rounded-full text-sm font-semibold mb-4">
            Il Nostro Impegno
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#2B7A9F] mb-6">
            Perché siamo una{' '}
            <span className="text-[#5FB76E]">Società Benefit</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Non parliamo solo di assicurazioni. Il nostro status di <strong>Società Benefit</strong> ci 
            impegna per legge a generare un impatto positivo sulla società, operando in modo 
            responsabile, sostenibile e trasparente.
          </p>
        </div>

        {/* Founders Story Box */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16 border border-gray-100 animate-fade-in-up">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-[#F9D424]/20 text-[#E67E22] rounded-full text-xs font-semibold mb-4">
                La nostra storia
              </span>
              <h3 className="text-2xl font-bold text-[#2B7A9F] mb-4">
                35 anni di volontariato ci hanno insegnato cosa significa proteggere chi si prende cura degli altri
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Assietica nasce dall'esperienza di <strong>Mauro Antonio Mura</strong>, volontario (per vocazione) impegnato nel sostegno alle persone con disabilità intellettiva, 
                e di <strong>Umberto Cossu</strong>, attivo da decenni nel soccorso sanitario e dell'assistenza tecnico sportiva. 
                L'impegno maturato come volontari non è un dettaglio biografico: è la nostra identità, la forza che orienta ogni scelta e ogni consulenza.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link 
                  to="/societa-benefit"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#2B7A9F] to-[#3AAFA9] text-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <span>Scopri l'Identità Benefit</span>
                  <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/societa-cooperativa"
                  className="inline-flex items-center space-x-2 bg-white text-[#5FB76E] border-2 border-[#5FB76E] px-5 py-2.5 rounded-lg font-semibold hover:bg-[#5FB76E]/5 transition-all duration-300"
                >
                  <span>Scopri l'Anima Cooperativa</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-[#2B7A9F] to-[#3AAFA9] rounded-xl p-6 text-white text-center">
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-3 border-2 border-white/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/ta3cssnp_WhatsApp%20Image%202026-02-26%20at%2018.22.20.jpeg" 
                    alt="Mauro Antonio Mura"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="font-bold">Mauro Antonio Mura</h4>
                <p className="text-white/80 text-xs mt-1">Volontario - Disabilità</p>
              </div>
              <div className="bg-gradient-to-br from-[#5FB76E] to-[#3AAFA9] rounded-xl p-6 text-white text-center">
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-3 border-2 border-white/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/dukhogve_WhatsApp%20Image%202026-02-26%20at%2018.25.38.jpeg" 
                    alt="Umberto Cossu"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="font-bold">Umberto Cossu</h4>
                <p className="text-white/80 text-xs mt-1">Volontario - Soccorso</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Left Column - Image Grid */}
          <div className="grid grid-cols-2 gap-4 animate-fade-in-up">
            <div className="space-y-4">
              <img
                src={benefitImages[0]}
                alt="Comunità"
                className="w-full h-64 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
              />
              <img
                src={benefitImages[1]}
                alt="Volontariato"
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
              />
            </div>
            <div className="space-y-4 pt-12">
              <img
                src={benefitImages[2]}
                alt="Impatto Sociale"
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
              />
              <div className="bg-gradient-to-br from-[#5FB76E] to-[#3AAFA9] rounded-xl p-6 text-white shadow-lg h-64 flex flex-col justify-center">
                <h3 className="text-3xl font-bold mb-2">100%</h3>
                <p className="text-lg">Impegno per il beneficio comune</p>
              </div>
            </div>
          </div>

          {/* Right Column - Values */}
          <div className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {percheSocietaBenefit.map((valore, index) => {
              const IconComponent = iconMap[valore.icona];
              return (
                <div
                  key={index}
                  className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#5FB76E]/20 to-[#3AAFA9]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-[#5FB76E]" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#5FB76E] transition-colors duration-300">
                        {valore.titolo}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {valore.descrizione}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quote Section */}
        <div className="bg-gradient-to-r from-[#5FB76E] to-[#3AAFA9] rounded-2xl p-8 sm:p-12 text-white shadow-2xl animate-fade-in-up">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="text-white" size={32} />
            </div>
            <blockquote className="text-2xl sm:text-3xl font-bold mb-6 leading-relaxed">
              "Essere Società Benefit significa che il nostro successo si misura anche 
              dall'impatto positivo che generiamo sulle comunità che serviamo"
            </blockquote>
            <p className="text-lg text-white/90 mb-8">
              La nostra certificazione ci impone trasparenza, misurabilità e rendicontazione 
              annuale del beneficio comune generato. Non è marketing, è un impegno legale.
            </p>
            <div className="flex flex-wrap justify-center gap-6 pt-6 border-t border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">Agenzia</div>
                <div className="text-white/80 text-sm">Assimoco accreditata Eticapro</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">100%</div>
                <div className="text-white/80 text-sm">Cooperativa</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">Certificata</div>
                <div className="text-white/80 text-sm">Società Benefit</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocietaBenefitSection;
